import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { Community, UNKNOWN_COMMUNITY } from "@/types/community";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import CancelCreate from '@/components/CancelCreate';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    maxWidth: 500,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
});

export default function Background() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const backgroundInput = useRef<any>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const userInfo = useSelector((state: RootState) => state.user.user_info);
  const { id } = useLocalSearchParams<{ id?: string }>();

  const { register, control, handleSubmit, setValue, getValues, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      background: '',
    }
  });

  const background = watch("background");

  const loadInitialData = useCallback(async () => {
    try {
      showSpinner();
      
      const response = await AppUtilities.fetchAuthorized(
        "Background.loadInitialData",
        "/rabbl/api/adminrabbl",
        "POST",
        {
          id: id,
        }
      );
      const communityResult: Community = ((response as any).body as any).community;

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => loadInitialData(), true);
      AppUtilities.handleException(e, "Background.loadInitialData");
    } finally {
      hideSpinner();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadInitialData();
    }
  }, [id]);

  async function onSubmit(data: any) {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "Background.onSubmit",
        "/rabbl/api/set_rabbl_background",
        "POST",
        {
          background: data.background,
          id: id,
        }
      );
      const creating_community: Community = ((response as any).body as any).creating_community;
      dispatch(setData(response));
      if (id && id.length > 0) {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.navigate("/rabbls");
        }
      } else {
        router.navigate(AppUtils.getRabblCreateNextPageLink(creating_community, userInfo) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Background.onSubmit");
    } finally {
      hideSpinner();
    }
  };

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (backgroundInput && backgroundInput.current) {
        backgroundInput.current?.focus();
      }
    }
  }, [backgroundInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Background"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <AppText>Describe how you'll promote your community (e.g. social media, etc.) and how big your community is. You can include links to your social media profiles.</AppText>
          <AppText>This will only be seen by Rabbl administrators to evaluate whether to approve your Rabbl.</AppText>

          <Controller
            control={control}
            name="background"
            rules={{
              validate: (x) => {
                if (x == '') {
                  return 'Rabbl community background is required.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <TextInput
                  ref={backgroundInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Community background"
                  style={styles.formElement}
                  returnKeyType="go"
                  onBlur={onBlur}
                  multiline={true}
                  numberOfLines={4}
                  inputStyle={AppConstants.STYLES.textInputMultiline}
                />
              </View>
            )}
          />
          {errors.background && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.background.message} ^</AppText>}

          <View style={styles.buttonWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)} disabled={AppUtilities.isBlank(background)}>Next</Button95>
          </View>
        </View>
      </Panel>
      
      { (id == null || id.length == 0) && <CancelCreate /> }
      
      <Footer />
    </BasicScrollView>
  );
}
